/**
 * Dashboard Chart Data Seeding Script
 *
 * 過去90日間のモックデータをLocalStorageに投入
 * 実行方法: npm run seed:dashboard
 */

import { generateAllMockData } from '../utils/mockDataGenerator';
import * as LocalStorageService from '../utils/localStorage';
import type { ExtendedReservation } from '../types/salonboard';

/**
 * 既存データ確認
 */
function checkExistingData(): { hasData: boolean; counts: { customers: number; reservations: number; treatments: number } } {
  const customers = LocalStorageService.getCustomers();
  const reservations = LocalStorageService.getReservations();
  const treatments = LocalStorageService.getTreatments();

  return {
    hasData: customers.length > 0 || reservations.length > 0 || treatments.length > 0,
    counts: {
      customers: customers.length,
      reservations: reservations.length,
      treatments: treatments.length,
    },
  };
}

/**
 * データをクリア
 */
function clearAllData() {
  console.log('🗑️  既存データをクリアしています...');

  // LocalStorageを直接クリア
  localStorage.removeItem('salonhub_customers');
  localStorage.removeItem('salonhub_reservations');
  localStorage.removeItem('salonhub_treatments');
  localStorage.removeItem('salonhub_sequences');

  console.log('✅ データクリア完了');
}

/**
 * Reservation型をExtendedReservation型に変換
 */
function convertToExtendedReservations(reservations: any[]): ExtendedReservation[] {
  return reservations.map(r => ({
    ...r,
    // ExtendedReservation固有のフィールドを追加
    externalId: undefined,
    externalSource: undefined,
    syncedAt: undefined,
    metadata: undefined,
  }));
}

/**
 * メインシーディング関数
 */
export async function seedDashboardData(force: boolean = false) {
  console.log('\n🌱 ダッシュボードデータシーディング開始...\n');

  // 既存データ確認
  const existing = checkExistingData();

  if (existing.hasData && !force) {
    console.log('⚠️  既存データが検出されました:');
    console.log(`   顧客: ${existing.counts.customers}件`);
    console.log(`   予約: ${existing.counts.reservations}件`);
    console.log(`   施術: ${existing.counts.treatments}件`);
    console.log('\n💡 既存データを上書きする場合は、force=true を指定してください。');
    console.log('   例: seedDashboardData(true)');
    return;
  }

  if (force && existing.hasData) {
    console.log('🔄 強制モード: 既存データを上書きします');
    clearAllData();
  }

  // モックデータ生成
  console.log('');
  const { customers, reservations, treatments, stats } = generateAllMockData();

  // LocalStorageに保存
  console.log('\n💾 LocalStorageへ保存中...');

  try {
    // 顧客データ保存
    LocalStorageService.saveCustomers(customers);
    console.log(`✅ 顧客データ保存完了: ${customers.length}件`);

    // 予約データ保存（ExtendedReservation型に変換）
    const extendedReservations = convertToExtendedReservations(reservations);
    LocalStorageService.saveReservations(extendedReservations);
    console.log(`✅ 予約データ保存完了: ${reservations.length}件`);

    // 施術データ保存
    LocalStorageService.saveTreatments(treatments);
    console.log(`✅ 施術データ保存完了: ${treatments.length}件`);

    // 成功メッセージ
    console.log('\n🎉 データシーディング完了！\n');
    console.log('📊 投入データサマリー:');
    console.log('━━━━━━━━━━━━━━━━━━━━━━');
    console.log(`  総顧客数: ${customers.length}件`);
    console.log(`    - 新規: ${stats.newCustomers}件 (${Math.round(stats.newCustomers / customers.length * 100)}%)`);
    console.log(`    - リピーター: ${stats.repeatCustomers}件 (${Math.round(stats.repeatCustomers / customers.length * 100)}%)`);
    console.log('');
    console.log(`  総予約数: ${reservations.length}件`);
    console.log(`    - 完了: ${stats.completedReservations}件`);
    console.log(`    - 確定: ${reservations.filter(r => r.status === 'confirmed').length}件`);
    console.log(`    - キャンセル: ${reservations.filter(r => r.status === 'cancelled').length}件`);
    console.log('');
    console.log(`  総施術数: ${treatments.length}件`);
    console.log(`  総売上: ¥${stats.totalRevenue.toLocaleString()}`);
    console.log(`  平均客単価: ¥${stats.avgSpending.toLocaleString()}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━');
    console.log('\n✨ ダッシュボードを開いてチャートを確認してください！');
    console.log('   URL: http://localhost:3001/dashboard\n');

  } catch (error) {
    console.error('❌ データ保存中にエラーが発生しました:', error);
    throw error;
  }
}

// ブラウザコンソールから実行可能にする
if (typeof window !== 'undefined') {
  (window as any).seedDashboardData = seedDashboardData;
  console.log('💡 ブラウザコンソールから実行できます:');
  console.log('   seedDashboardData()      // 既存データがある場合はスキップ');
  console.log('   seedDashboardData(true)  // 既存データを上書き');
}

export default seedDashboardData;
